import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Shield, Key, Lock, Check, X, AlertCircle, Loader2, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProfile = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { navigate('/admin/login'); return; }
    loadMe();
  }, []);

  const loadMe = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (r.status === 401) { navigate('/admin/login'); return; }
      const d = await r.json();
      setMe(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading || !me) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 size={32} className="animate-spin text-red-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3 mb-6">
          <User className="text-red-600" /> Admin Profil
        </h1>

        {/* Info card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white text-2xl font-bold">
              {me.username?.[0]?.toUpperCase() || 'A'}
            </div>
            <div>
              <p className="text-xs text-gray-500">Kullanıcı adı</p>
              <p className="text-lg font-bold text-gray-800">{me.username}</p>
              <p className="text-xs text-gray-500 mt-1">Yönetici</p>
            </div>
          </div>
        </div>

        <ChangePasswordCard token={token} />
        <TwoFactorCard token={token} enabled={me.totp_enabled} onChange={loadMe} />
      </div>
    </AdminLayout>
  );
};

// ============== Change Password ==============
const ChangePasswordCard = ({ token }) => {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (form.next !== form.confirm) {
      setMsg({ type: 'error', text: 'Yeni şifre ve onay eşleşmiyor' });
      return;
    }
    if (form.next.length < 8) {
      setMsg({ type: 'error', text: 'Yeni şifre en az 8 karakter olmalı' });
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: form.current, new_password: form.next })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Hata');
      setMsg({ type: 'success', text: d.message });
      setForm({ current: '', next: '', confirm: '' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h2 className="font-bold text-gray-800 flex items-center gap-2 mb-4">
        <Key size={20} className="text-red-600" /> Şifre Değiştir
      </h2>
      {msg && (
        <div data-testid="pw-change-msg" className={`rounded p-3 mb-4 text-sm ${
          msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {msg.text}
        </div>
      )}
      <form onSubmit={submit} className="space-y-3">
        <input
          type="password" placeholder="Mevcut şifre" required
          value={form.current} onChange={e => setForm({ ...form, current: e.target.value })}
          data-testid="pw-current"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        />
        <input
          type="password" placeholder="Yeni şifre (en az 8 karakter)" required
          value={form.next} onChange={e => setForm({ ...form, next: e.target.value })}
          data-testid="pw-next"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        />
        <input
          type="password" placeholder="Yeni şifre (tekrar)" required
          value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })}
          data-testid="pw-confirm"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        />
        <button type="submit" disabled={submitting}
          data-testid="pw-submit-btn"
          className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50 flex items-center gap-2"
        >
          {submitting && <Loader2 size={16} className="animate-spin" />}
          {submitting ? 'Güncelleniyor...' : 'Şifreyi Değiştir'}
        </button>
      </form>
    </div>
  );
};

// ============== 2FA Card ==============
const TwoFactorCard = ({ token, enabled, onChange }) => {
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [disablePw, setDisablePw] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const beginSetup = async () => {
    setLoading(true);
    setMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/2fa/setup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Hata');
      setSetupData(d);
      setCode('');
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/2fa/verify-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ code })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Kod hatalı');
      setMsg({ type: 'success', text: d.message });
      setSetupData(null);
      setCode('');
      onChange();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/2fa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ password: disablePw })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Hata');
      setMsg({ type: 'success', text: d.message });
      setShowDisable(false);
      setDisablePw('');
      onChange();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="font-bold text-gray-800 flex items-center gap-2 mb-1">
        <Shield size={20} className={enabled ? 'text-green-600' : 'text-gray-400'} />
        Google İki Aşamalı Doğrulama (2FA)
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Google Authenticator, Microsoft Authenticator veya Authy uygulamasıyla ek güvenlik.
      </p>

      <div className={`rounded p-3 mb-4 flex items-center gap-2 ${enabled ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
        {enabled ? <><Check size={18} /> <strong>Aktif</strong> — Girişte 6 haneli kod istenir</> : <><X size={18} /> Pasif — Sadece şifre ile giriş yapılıyor</>}
      </div>

      {msg && (
        <div data-testid="2fa-msg" className={`rounded p-3 mb-4 text-sm ${
          msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {msg.text}
        </div>
      )}

      {!enabled && !setupData && (
        <button
          onClick={beginSetup}
          disabled={loading}
          data-testid="2fa-start-setup-btn"
          className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
          2FA'yı Etkinleştir
        </button>
      )}

      {!enabled && setupData && (
        <div className="border rounded-lg p-4 bg-gray-50">
          <h3 className="font-semibold text-gray-800 mb-3">📱 Kurulum Adımları</h3>
          <ol className="text-sm text-gray-700 space-y-2 mb-4 list-decimal pl-5">
            <li>Telefonunuza <strong>Google Authenticator</strong> uygulamasını kurun (App Store / Play Store)</li>
            <li>Uygulamada "+" → "QR kodu tara"ya basın</li>
            <li>Aşağıdaki QR kodu tarayın</li>
            <li>Uygulamada görünen 6 haneli kodu girip "Doğrula" butonuna basın</li>
          </ol>

          <div className="flex flex-col items-center mb-4">
            <div className="bg-white p-3 rounded-lg border">
              <img src={setupData.qr_code_url} alt="2FA QR Code" className="w-60 h-60" data-testid="2fa-qr-code" />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              QR tarayamıyorsanız manuel kod: <code className="bg-gray-200 px-2 py-1 rounded font-mono text-xs" data-testid="2fa-secret">{setupData.secret}</code>
            </p>
          </div>

          <form onSubmit={verifySetup} className="flex gap-2">
            <input
              type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required
              placeholder="6 haneli kod"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              data-testid="2fa-setup-code"
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-center text-lg font-mono tracking-widest focus:ring-2 focus:ring-green-500"
            />
            <button type="submit" disabled={loading || code.length !== 6}
              data-testid="2fa-verify-setup-btn"
              className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
            >
              {loading && <Loader2 size={16} className="animate-spin" />} Doğrula ve Aktifleştir
            </button>
          </form>
        </div>
      )}

      {enabled && !showDisable && (
        <button
          onClick={() => setShowDisable(true)}
          data-testid="2fa-disable-toggle-btn"
          className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold text-sm"
        >
          2FA'yı Devre Dışı Bırak
        </button>
      )}

      {enabled && showDisable && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4">
          <div className="flex items-start gap-2 mb-3">
            <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">
              2FA'yı kapatmak güvenliğinizi azaltır. Devam etmek için şifrenizi girin.
            </p>
          </div>
          <form onSubmit={disable} className="flex gap-2">
            <input
              type="password" placeholder="Admin şifreniz" required
              value={disablePw}
              onChange={e => setDisablePw(e.target.value)}
              data-testid="2fa-disable-pw"
              className="flex-1 px-4 py-2.5 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500"
            />
            <button type="submit" disabled={loading}
              data-testid="2fa-disable-btn"
              className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50"
            >
              Devre Dışı Bırak
            </button>
            <button type="button"
              onClick={() => { setShowDisable(false); setDisablePw(''); }}
              className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              İptal
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminProfile;
